import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import StructuredData from '../../components/feature/StructuredData';

const ContactoPage = () => {
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    telefono: '',
    servicio: '',
    mensaje: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    document.title = 'Contacto - ECOSERVEIS | Vaciado de Locales Barcelona ☎ 644 613 451';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Contacta con ECOSERVEIS para vaciado de locales Barcelona. Av. Nord 11 B-2, L\'Hospitalet. ☎ 644 613 451. Presupuesto sin compromiso en 24h.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Contacta con ECOSERVEIS para vaciado de locales Barcelona. Av. Nord 11 B-2, L\'Hospitalet. ☎ 644 613 451. Presupuesto sin compromiso en 24h.';
      document.head.appendChild(meta);
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'contacto vaciado locales Barcelona, presupuesto sin compromiso, teléfono ECOSERVEIS, dirección L\'Hospitalet');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'keywords';
      meta.content = 'contacto vaciado locales Barcelona, presupuesto sin compromiso, teléfono ECOSERVEIS, dirección L\'Hospitalet';
      document.head.appendChild(meta);
    }
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    // Validación
    if (!formData.nombre || !formData.email || !formData.telefono || !formData.servicio || !formData.mensaje) {
      setSubmitStatus('error');
      setIsSubmitting(false);
      return;
    }

    if (formData.mensaje.length > 500) {
      setSubmitStatus('error');
      setIsSubmitting(false);
      return;
    }

    try {
      const formBody = new URLSearchParams();
      formBody.append('nombre', formData.nombre);
      formBody.append('email', formData.email);
      formBody.append('telefono', formData.telefono);
      formBody.append('servicio', formData.servicio);
      formBody.append('mensaje', formData.mensaje);

      const response = await fetch('https://readdy.ai/api/form/d5jn8hqgbpr25eot49n0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          nombre: '',
          email: '',
          telefono: '',
          servicio: '',
          mensaje: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <StructuredData type="home" />
      <Header />
      <WhatsAppButton />
      
      <main>
        {/* Hero Section */}
        <section className="pt-32 pb-20 lg:pt-36 lg:pb-20 bg-gradient-to-br from-gray-50 to-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="inline-flex items-center px-4 py-2 bg-white border border-gray-200 rounded-full mb-6">
              <div className="w-5 h-5 flex items-center justify-center mr-2">
                <i className="ri-mail-line text-[#FF6B35]"></i>
              </div>
              <span className="text-sm font-medium text-gray-700">Contacto</span>
            </div>
            
            <h1 className="font-['Sora'] text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
              Contacta con nosotros
            </h1>
            
            <p className="text-xl text-gray-600 leading-relaxed mb-8">
              Contacta con nosotros para <strong>vaciado de locales Barcelona</strong>. Te respondemos en menos de 24 horas con un presupuesto personalizado. <Link to="/servicios" className="text-[#FF6B35] hover:underline font-semibold">Consulta todos nuestros servicios</Link>.
            </p>
          </div>
        </section>

        {/* Contacto y Formulario */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12">
              {/* Información de Contacto */}
              <div>
                <h2 className="font-['Sora'] text-3xl font-bold text-gray-900 mb-6">
                  Información de Contacto
                </h2>

                <div className="space-y-6 mb-8">
                  <div className="flex items-start">
                    <div className="w-12 h-12 flex items-center justify-center bg-[#FF6B35]/10 rounded-lg mr-4 flex-shrink-0">
                      <i className="ri-map-pin-2-fill text-2xl text-[#FF6B35]"></i>
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-1">Dirección</h3>
                      <a
                        href="https://maps.app.goo.gl/kGNXARTnHrknyJRDA"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-600 hover:text-[#FF6B35] transition-colors cursor-pointer"
                      >
                        Av. Nord 11 B-2<br />
                        08905 L'Hospitalet de Llobregat<br />
                        Barcelona, España
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="w-12 h-12 flex items-center justify-center bg-[#FF6B35]/10 rounded-lg mr-4 flex-shrink-0">
                      <i className="ri-phone-fill text-2xl text-[#FF6B35]"></i>
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-1">Teléfono</h3>
                      <a
                        href="tel:+34644613451"
                        className="text-gray-600 hover:text-[#FF6B35] transition-colors text-lg cursor-pointer"
                      >
                        +34 644 613 451
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="w-12 h-12 flex items-center justify-center bg-[#FF6B35]/10 rounded-lg mr-4 flex-shrink-0">
                      <i className="ri-whatsapp-fill text-2xl text-[#FF6B35]"></i>
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-1">WhatsApp</h3>
                      <a
                        href="https://wa.me/34644613451"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-600 hover:text-[#FF6B35] transition-colors cursor-pointer"
                      >
                        Enviar mensaje
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="w-12 h-12 flex items-center justify-center bg-[#FF6B35]/10 rounded-lg mr-4 flex-shrink-0">
                      <i className="ri-time-line text-2xl text-[#FF6B35]"></i>
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-1">Horario</h3>
                      <p className="text-gray-600">
                        Consultar disponibilidad<br />
                        Nos adaptamos a tus necesidades
                      </p>
                    </div>
                  </div>
                </div>

                {/* Mapa */}
                <div className="rounded-2xl overflow-hidden shadow-xl">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2994.0234567890123!2d2.1098!3d41.3588!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDHCsDIxJzMxLjciTiAywrAwNiczNS4zIkU!5e0!3m2!1ses!2ses!4v1234567890123!5m2!1ses!2ses"
                    width="100%"
                    height="400"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Ubicación ECOSERVEIS en Barcelona"
                  ></iframe>
                </div>
              </div>

              {/* Formulario */}
              <div>
                <div className="bg-white rounded-2xl shadow-xl p-8">
                  <h2 className="font-['Sora'] text-3xl font-bold text-gray-900 mb-6">
                    Solicita tu presupuesto
                  </h2>

                  <form id="contacto-form" data-readdy-form onSubmit={handleSubmit} className="space-y-5">
                    <div>
                      <label htmlFor="nombre" className="block text-sm font-semibold text-gray-700 mb-2">
                        Nombre completo *
                      </label>
                      <input
                        type="text"
                        id="nombre"
                        name="nombre"
                        value={formData.nombre}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B35] focus:border-[#FF6B35] outline-none transition-all"
                        placeholder="Tu nombre"
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B35] focus:border-[#FF6B35] outline-none transition-all"
                        placeholder="tu@email.com"
                      />
                    </div>

                    <div>
                      <label htmlFor="telefono" className="block text-sm font-semibold text-gray-700 mb-2">
                        Teléfono *
                      </label>
                      <input
                        type="tel"
                        id="telefono"
                        name="telefono"
                        value={formData.telefono}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B35] focus:border-[#FF6B35] outline-none transition-all"
                        placeholder="+34 600 000 000"
                      />
                    </div>

                    <div>
                      <label htmlFor="tipo-inmueble" className="block text-sm font-semibold text-gray-900 mb-2">
                        Tipo de Inmueble *
                      </label>
                      <select
                        id="tipo-inmueble"
                        name="tipo-inmueble"
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B35] focus:border-transparent text-[15px]"
                      >
                        <option value="">Selecciona una opción</option>
                        <option value="Local comercial">Local comercial</option>
                        <option value="Nave industrial">Nave industrial</option>
                        <option value="Oficina">Oficina</option>
                        <option value="Despacho">Despacho</option>
                        <option value="Almacén">Almacén</option>
                        <option value="Trastero">Trastero</option>
                        <option value="Garaje">Garaje</option>
                        <option value="Terreno">Terreno</option>
                        <option value="Otro">Otro</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="mensaje" className="block text-sm font-semibold text-gray-700 mb-2">
                        Mensaje *
                      </label>
                      <textarea
                        id="mensaje"
                        name="mensaje"
                        value={formData.mensaje}
                        onChange={handleChange}
                        required
                        maxLength={500}
                        rows={5}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6B35] focus:border-[#FF6B35] outline-none transition-all resize-none"
                        placeholder="Cuéntanos qué necesitas: ubicación, tipo de local, volumen aproximado..."
                      ></textarea>
                      <p className="text-xs text-gray-500 mt-1">
                        {formData.mensaje.length}/500 caracteres
                      </p>
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full px-8 py-4 bg-[#FF6B35] text-white text-lg font-semibold rounded-lg hover:bg-[#E55A2B] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                    >
                      {isSubmitting ? 'Enviando...' : 'Enviar Solicitud'}
                    </button>

                    {submitStatus === 'success' && (
                      <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                        <p className="text-green-800 text-sm font-medium">
                          ¡Mensaje enviado correctamente! Te responderemos en menos de 24 horas.
                        </p>
                      </div>
                    )}

                    {submitStatus === 'error' && (
                      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-red-800 text-sm font-medium">
                          Error al enviar el mensaje. Por favor, inténtalo de nuevo o llámanos directamente.
                        </p>
                      </div>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Zona de Servicio */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="font-['Sora'] text-4xl font-bold text-gray-900 mb-4">
                Zona de Servicio
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Realizamos <strong>vaciado de locales comerciales</strong>, <strong>vaciado de naves industriales Barcelona</strong> y <strong>vaciado de oficinas</strong> en toda Barcelona y provincia. <Link to="/empresa" className="text-[#FF6B35] hover:underline font-semibold">Conoce nuestra empresa y trayectoria</Link>.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-white rounded-xl p-6 shadow-md">
                <h3 className="font-bold text-gray-900 mb-3 flex items-center">
                  <div className="w-6 h-6 flex items-center justify-center mr-2">
                    <i className="ri-map-pin-fill text-[#FF6B35]"></i>
                  </div>
                  Barcelona Capital
                </h3>
                <p className="text-sm text-gray-600">
                  Ciutat Vella, Eixample, Sants-Montjuïc, Les Corts, Sarrià–Sant Gervasi, Gràcia, Horta-Guinardó, Nou Barris, Sant Andreu, Sant Martí. <Link to="/servicios" className="text-[#FF6B35] hover:underline">Ver servicios disponibles</Link>.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-md">
                <h3 className="font-bold text-gray-900 mb-3 flex items-center">
                  <div className="w-6 h-6 flex items-center justify-center mr-2">
                    <i className="ri-map-pin-fill text-[#FF6B35]"></i>
                  </div>
                  Área Metropolitana
                </h3>
                <p className="text-sm text-gray-600">
                  L'Hospitalet, Badalona, Santa Coloma, Cornellà, El Prat, Sant Boi, Gavà, Viladecans, Castelldefels, Esplugues, Sant Just, Sant Joan Despí. <Link to="/faq" className="text-[#FF6B35] hover:underline">Consulta preguntas frecuentes</Link>.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-md">
                <h3 className="font-bold text-gray-900 mb-3 flex items-center">
                  <div className="w-6 h-6 flex items-center justify-center mr-2">
                    <i className="ri-map-pin-fill text-[#FF6B35]"></i>
                  </div>
                  Provincia Barcelona
                </h3>
                <p className="text-sm text-gray-600">
                  Barberà, Cerdanyola, Ripollet, Montcada, Sant Cugat, Rubí, Terrassa, Sabadell, Granollers, Mollet, Mataró, Martorell y cercanías. <Link to="/" className="text-[#FF6B35] hover:underline">Volver al inicio</Link>.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Final */}
        <section className="py-20 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-['Sora'] text-4xl font-bold mb-6">
              ¿Prefieres Llamar Directamente?
            </h2>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Atención inmediata por teléfono o WhatsApp. Te atendemos personalmente para resolver tus dudas sobre <strong>fin de alquiler</strong>, <strong>cierre</strong>, <strong>traspaso</strong> o <strong>reforma</strong>. <Link to="/empresa" className="text-white hover:text-gray-200 underline">Conoce nuestra empresa</Link>.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:+34644613451"
                className="inline-flex items-center justify-center px-8 py-4 bg-[#FF6B35] text-white text-lg font-semibold rounded-lg hover:bg-[#E55A2B] transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                <div className="w-5 h-5 flex items-center justify-center mr-2">
                  <i className="ri-phone-fill"></i>
                </div>
                Llamar: 644 613 451
              </a>
              <a
                href="https://wa.me/34644613451"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-8 py-4 bg-white text-gray-900 text-lg font-semibold rounded-lg hover:bg-gray-100 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                <div className="w-5 h-5 flex items-center justify-center mr-2">
                  <i className="ri-whatsapp-line"></i>
                </div>
                WhatsApp
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default ContactoPage;